import { z } from 'zod';
import { PaymentOptionSchema } from '../collectors/index.js';
import { CaseVoucherSchema } from '../economy/case-vouchers/index.js';
import { FileSchema } from '../files/index.js';
import { AddressSchema, Entity, PhoneSchema } from '../index.js';
import { IntentSchema } from '../intents/index.js';
import { InteractionTagSchema } from '../interaction-tags/index.js';
import { PerformedBySchema } from '../performed-by/index.js';
import { SproomReceiverSchema, SproomSenderSchema } from '../sproom/index.js';
import { userRoles } from '../users/roles.js';
import { EBoksRecipientSchema } from './eboks.js';
import { AutoCallEventSchema, EBoksEventSchema, EInvoiceEventSchema, EmailEventSchema, InteractionEventSchema, LetterEventSchema, SmsEventSchema, SystemInteractionEventSchema, } from './events/index.js';
import { Direction, IncomingState, InteractionType, OutgoingState, } from './index.js';
export const MAX_CREDITOR_MESSAGE_RECIPIENTS = 50;
// Duplicates never count against the cap, which applies to unique recipients.
export const creditorMessageRecipientsSchema = z
    .array(z.uuid())
    .transform((ids) => [...new Set(ids)])
    .refine((ids) => ids.length <= MAX_CREDITOR_MESSAGE_RECIPIENTS, {
    message: `A creditor message can notify at most ${MAX_CREDITOR_MESSAGE_RECIPIENTS} recipients.`,
});
// Interaction category schema
const InteractionCategorySchema = z.object({
    id: z.string(),
    createdBy: PerformedBySchema,
});
// Details schemas
const EmailDetailsSchema = z.object({
    server: z.string().nullable(),
    imapId: z.string().nullish(),
    subject: z.string(),
    from: z.string(),
    to: z.string(),
    cc: z.string().nullish(),
    bcc: z.string().nullish(),
    replyTo: z.string().nullish(),
    body: z.string(),
    html: z.string().nullable(),
    inReplyTo: z.string().nullish(),
    references: z.string().nullish(),
});
const LetterDetailsSchema = z.object({
    pdf: z.string(),
    originalPdf: z.string(),
    address: AddressSchema.nullable(),
    body: z.string().nullish(),
    pageCount: z.number().optional(),
});
const PhoneDetailsSchema = z.object({
    phone: PhoneSchema.nullable(),
    tags: z.array(z.object({ tag: z.string() })).nullable(),
});
const NoteDetailsSchema = z.object({});
const ClientNoteDetailsSchema = z.object({
    recipients: z.array(z.uuid()).optional(),
});
const CreateClientNoteDetailsSchema = z.object({
    recipients: creditorMessageRecipientsSchema.optional(),
});
// Loose because bespoke imports add their own keys (e.g. APCOA's
// debtorMessage). recipient is missing on messages sent before it was stored.
const DebtorNoteDetailsSchema = z.looseObject({
    recipient: z
        .object({
        name: z.string().nullable(),
        email: z.string().nullable(),
    })
        .optional(),
});
// SMSDetails.data is intentionally untyped as it contains provider-specific data
const SMSDetailsSchema = z.object({
    from: z.string(),
    number: z.string(),
    text: z.string(),
    countryCode: z.string(),
    data: z.record(z.string(), z.unknown()).optional(),
});
const AutoCallDetailsSchema = z.object({
    fromCountryCode: z.string(),
    fromPhoneNumber: z.string(),
    toCountryCode: z.string(),
    toPhoneNumber: z.string(),
    transcript: z.string(),
    voiceName: z.string(),
    actions: z.array(z.object({ procedureId: z.string(), id: z.string() })),
});
const EBoksDetailsSchema = z.object({
    title: z.string(),
    pdf: z.string(),
    button: z
        .object({ title: z.string(), description: z.string(), url: z.string() })
        .nullable()
        .optional(),
    recipient: EBoksRecipientSchema,
    body: z.string().nullish(),
});
const KivraDetailsSchema = z.object({
    title: z.string(),
    pdf: z.string(),
    button: z
        .object({ title: z.string(), description: z.string(), url: z.string() })
        .nullable()
        .optional(),
    recipient: EBoksRecipientSchema,
    body: z.string().nullish(),
});
const DigipostDetailsSchema = z.object({
    title: z.string(),
    pdf: z.string(),
    button: z
        .object({ title: z.string(), description: z.string(), url: z.string() })
        .nullable()
        .optional(),
    recipient: EBoksRecipientSchema,
    body: z.string().nullish(),
});
const EInvoiceDetailsSchema = z.object({
    vouchers: z.array(CaseVoucherSchema),
    sender: SproomSenderSchema,
    receiver: SproomReceiverSchema,
    collectorPaymentOptions: z.array(PaymentOptionSchema),
});
// Even channel-less interactions receive system events (journalization,
// approval, link tracking), so no interaction type has a truly empty events
// contract.
const SystemEventsSchema = z.array(SystemInteractionEventSchema);
const BaseInteractionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(InteractionType.Email),
        details: EmailDetailsSchema,
        events: z.array(EmailEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.Eboks),
        details: EBoksDetailsSchema,
        events: z.array(EBoksEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.Kivra),
        details: EBoksDetailsSchema,
        events: SystemEventsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Digipost),
        details: EBoksDetailsSchema,
        events: SystemEventsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Letter),
        details: LetterDetailsSchema,
        events: z.array(LetterEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.Phone),
        details: PhoneDetailsSchema,
        events: SystemEventsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.SMS),
        details: SMSDetailsSchema,
        events: z.array(SmsEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.Note),
        details: NoteDetailsSchema,
        events: SystemEventsSchema,
    }),
    // Notes trigger notification emails, so their delivery/bounce/open events
    // from SparkPost/Mailgun land on the note interaction itself.
    z.object({
        type: z.literal(InteractionType.ClientNote),
        details: ClientNoteDetailsSchema,
        events: z.array(EmailEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.DebtorNote),
        details: DebtorNoteDetailsSchema,
        events: z.array(EmailEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.AutoCall),
        details: AutoCallDetailsSchema,
        events: z.array(AutoCallEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.EInvoice),
        details: EInvoiceDetailsSchema,
        events: z.array(EInvoiceEventSchema),
    }),
    z.object({
        type: z.literal(InteractionType.Personal),
        details: NoteDetailsSchema,
        events: SystemEventsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Other),
        details: NoteDetailsSchema,
        events: SystemEventsSchema,
    }),
]);
export const InteractionSchema = z.intersection(BaseInteractionSchema, z.object({
    id: z.uuid(),
    seqId: z.number().optional(),
    tenantId: z.uuid(),
    referenceId: z.string().nullable(),
    referenceType: z.enum(Entity).nullable(),
    direction: z.enum(Direction),
    state: z.union([z.enum(OutgoingState), z.enum(IncomingState)]),
    attachments: z.array(FileSchema),
    debtor: z.string().nullish(),
    creditor: z.string().nullish(),
    notes: z.string().nullish(),
    transacted: z.string().nullable(),
    handler: z.string().nullable(),
    createdAt: z.string(),
    createdBy: PerformedBySchema.nullable(),
    minVisibilityRole: z.enum(userRoles),
    templateId: z.string().nullish(),
    templateVersionId: z.string().nullish(),
    migrated: z.boolean().optional(),
    intent: IntentSchema,
    interactionCategories: z.array(InteractionCategorySchema),
    tags: z.array(InteractionTagSchema.pick({
        id: true,
        name: true,
        hiddenByDefault: true,
    })),
    paused: z.number().nullish(),
    migratedId: z.string().nullable(),
    aiGenerated: z.boolean().optional(),
    aiAutoReplyId: z.string().nullish(),
}));
const CreateLetterDetailsSchema = z.union([
    z.object({
        address: AddressSchema.nullable(),
        body: z.string().optional(),
        pageCount: z.number().optional(),
    }),
    z.object({
        pdf: z.string(),
    }),
]);
const CreateEmailDetailsSchema = z.object({
    server: z.string().nullable(),
    imapId: z.string().nullish(),
    subject: z.string().optional(),
    from: z.string(),
    to: z.string(),
    cc: z.string().nullish(),
    bcc: z.string().nullish(),
    replyTo: z.string().nullish(),
    body: z.string().optional(),
    html: z.string().nullish(),
    inReplyTo: z.string().nullish(),
    references: z.string().nullish(),
});
const CreateEBoksDetailsSchema = z.object({
    title: z.string().optional(),
    pdf: z.string().optional(),
    button: z
        .object({ title: z.string(), description: z.string(), url: z.string() })
        .nullable()
        .optional(),
    recipient: EBoksRecipientSchema,
    body: z.string().nullish(),
});
const CreateSMSDetailsSchema = z.object({
    from: z.string(),
    number: z.string(),
    text: z.string().optional(),
    countryCode: z.string(),
    data: z.record(z.string(), z.unknown()).optional(),
});
const CreateAutoCallDetailsSchema = z.object({
    fromCountryCode: z.string(),
    fromPhoneNumber: z.string(),
    toCountryCode: z.string(),
    toPhoneNumber: z.string(),
    transcript: z.string().optional(),
    voiceName: z.string(),
    actions: z.array(z.object({ procedureId: z.string(), id: z.string() })),
});
const BaseCreateInteractionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(InteractionType.Email),
        details: CreateEmailDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Eboks),
        details: CreateEBoksDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Kivra),
        details: KivraDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Digipost),
        details: DigipostDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Letter),
        details: CreateLetterDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Phone),
        details: PhoneDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.SMS),
        details: CreateSMSDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Note),
        details: NoteDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.ClientNote),
        details: CreateClientNoteDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.DebtorNote),
        details: z.record(z.string(), z.unknown()),
    }),
    z.object({
        type: z.literal(InteractionType.AutoCall),
        details: CreateAutoCallDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.EInvoice),
        details: EInvoiceDetailsSchema,
    }),
    z.object({
        type: z.literal(InteractionType.Personal),
        details: z.object({}),
    }),
    z.object({
        type: z.literal(InteractionType.Other),
        details: z.object({}),
    }),
]);
export const APICreateInteractionSchema = z.intersection(BaseCreateInteractionSchema, z.object({
    referenceId: z
        .uuid()
        .nullish()
        .transform((x) => x ?? null),
    referenceType: z
        .enum(Entity)
        .nullish()
        .transform((x) => x ?? null),
    intent: IntentSchema.optional(),
    attachments: z.array(z.union([FileSchema, z.uuid()])).nullish(),
    state: z.union([z.enum(OutgoingState), z.enum(IncomingState)]).optional(),
    direction: z.enum(Direction),
    debtor: z.uuid().nullish(),
    notes: z.string().nullish(),
    minVisibilityRole: z.enum(userRoles),
    createdAt: z.string().nullish(),
    migrated: z.boolean().nullish(),
    templateId: z.uuid().nullish(),
    templateVersionId: z.uuid().nullish(),
    replyingToInteractionId: z.uuid().nullish(),
}));
export const CreateInteractionSchema = z.intersection(APICreateInteractionSchema, z.object({
    transacted: z.string().optional(),
    migratedId: z.string().optional(),
    tags: z.array(z.uuid()).optional(),
    createdBy: PerformedBySchema.nullish(),
    creditor: z.string().nullish(),
    events: z.array(InteractionEventSchema).optional(),
    aiGenerated: z.boolean().optional(),
    aiAutoReplyId: z.string().nullish(),
}));
//# sourceMappingURL=schemas.js.map